import { FormField } from '@/components/ui/form'
import { useContext, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormEntityContext } from '../../config/context/forms'
import Select, { SelectProps } from '../Select'
import { capitalize } from 'lodash'
import FieldWrapper from './FieldWrapper'
import { MultiSelect } from '@/components/ui/multi-select'

interface Props<T> extends Partial<Omit<SelectProps, 'options'>> {
  name: string
  options: readonly T[]
  isMulti?: boolean
  customLabel?: string
  customPlaceholder?: string
  customDescription?: string
  labelKey?: keyof T
  labelFn?: (option: T) => string
  valueKey?: keyof T
  transformOptions?: boolean
  layout?: 'box' | 'inline' | 'stacked' | 'inline-shrinkable'
  inputClassName?: string
}

const SelectField = <T,>({
  name,
  className,
  inputClassName,
  options,
  isMulti,
  customLabel,
  customDescription,
  customPlaceholder,
  labelKey,
  labelFn,
  valueKey,
  transformOptions = true,
  layout = 'inline',
  ...props
}: Props<T>) => {
  const { i18n, t } = useTranslation()
  const form = useFormContext()
  const { entityType, i18nPrefix } = useContext(FormEntityContext)

  const description =
    customDescription ??
    (i18n.exists(`${i18nPrefix}.${entityType}.${name}.description`)
      ? t(`${i18nPrefix}.${entityType}.${name}.description`)
      : undefined)

  const transformedOptions = useMemo(() => {
    return transformOptions
      ? options.map((option) => ({
          label: labelFn
            ? labelFn(option)
            : labelKey
            ? String(option[labelKey])
            : capitalize(String(option).replace('_', ' ').replace('_', ' ')),
          value: valueKey ? (option[valueKey] as string) : String(option),
        }))
      : options
  }, [options])

  const currentValue = form.watch(name)

  return (
    <div className={`w-full ${className}`}>
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => {
          return (
            <FieldWrapper
              layout={layout}
              label={
                customLabel ?? t(`${i18nPrefix}.${entityType}.${name}.label`)
              }
              description={description}
            >
              {isMulti ? (
                <MultiSelect<string>
                  options={
                    transformedOptions as { label: string; value: string }[]
                  }
                  value={field.value ?? []}
                  placeholder={customPlaceholder}
                  onChange={(value) => field.onChange(value)}
                />
              ) : (
                <Select
                  {...props}
                  value={currentValue}
                  options={
                    transformedOptions as { label: string; value: string }[]
                  }
                  placeholder={customPlaceholder}
                  className={`${
                    layout === 'box'
                      ? 'absolute right-3 top-3 w-40 bg-muted z-20 h-8'
                      : ''
                  } ${inputClassName ?? ''}`}
                  setValue={(value) => field.onChange(value ?? null)}
                />
              )}
            </FieldWrapper>
          )
        }}
      />
    </div>
  )
}

export default SelectField
